import { Button, Fab } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Col, Modal, ModalBody, ModalHeader, Row, Spinner } from "reactstrap";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setSelectedBarcodeSending, setShowSentBarcodesModal, startLoading, stopLoading } from "../../store/slices/appSlice";
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { useEffect, useState } from "react";
import { BarcodeDataToSaveDto, SentBarcodeAnimalDto, SentBarcodeDto, UpdatePregnancyCheckOrdersCommand } from "../../generated/tsClient";
import SentBarcode from "../components/SentBarcode";
import { fetchSentBarcodes, saveSentBarcodes } from "../../services/homeService";
import { toast } from "react-toastify";
import { getAutoCloseTime } from "../../utils/common";


function SentBarcodesModal(props: any) {
    const { t } = useTranslation(["texts", "errors"]);
    const dispatch = useAppDispatch();
    const showSentBarcodesModal = useAppSelector((state) => state.app.showSentBarcodesModal);
    const barcodeSending: SentBarcodeDto | null = useAppSelector((state) => state.app.selectedBarcodeSending);
    const selectedCowListOrder = useAppSelector((state) => state.app.selectedCowListOrder);
    const selectedHerd = useAppSelector((state) => state.app.selectedHerd);
    const isLoading = useAppSelector((state) => state.app.loading);
    const barcodeSendTo = useAppSelector((state) => state.app.selectedBarcodesSendTo);
    const [searchValue, setSearchValue] = useState('');
    const [currentAnimalList, setCurrentAnimalList] = useState<SentBarcodeAnimalDto[] | null>(null);

    useEffect(() => {
        doSearch();
    }, [searchValue, barcodeSending, selectedCowListOrder]);

    const toggleSentBarcodesModal = () => {
        if (showSentBarcodesModal)
            dispatch(setSelectedBarcodeSending(null));
        dispatch(setShowSentBarcodesModal(!showSentBarcodesModal));
    }

    const doSearch = () => {
        var newAnimalList;
        if (searchValue == null || searchValue.length === 0)
            newAnimalList = barcodeSending?.animals ?? null;
        else
            newAnimalList = barcodeSending?.animals?.filter(a =>
                (
                    a.nameShort?.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1
                    || a.earNr?.toString().indexOf(searchValue) !== -1
                    || a.euIdentifier?.substr(10, 4).indexOf(searchValue) !== -1
                )
            ) ?? null;

        setCurrentAnimalList(doSort(newAnimalList));
    };

    function compareNullableNumber(a: number | undefined, b: number | undefined): number {
        if (a == null || b == null) {
            if (a == null && b != null)
                return -1;
            if (a != null && b == null)
                return 1;
            return 0;
        }
        else
            return a - b;
    }

    function compareNullableString(a: string | undefined, b: string | undefined): number {
        if (a == null || b == null) {
            if (a == null && b != null)
                return -1;
            if (a != null && b == null)
                return 1;
            return 0;
        }
        else
            return (a > b) ? 1 : ((b > a) ? -1 : 0);
    }

    const doSort = (newAnimalList: SentBarcodeAnimalDto[] | null): SentBarcodeAnimalDto[] | null => {
        if (newAnimalList == null)
            return null;

        switch (selectedCowListOrder) {
            case 1:
                return [...newAnimalList].sort((a, b) => compareNullableNumber(a.earNr, b.earNr));
            case 2:
                return [...newAnimalList].sort((a, b) => compareNullableString(a.nameShort, b.nameShort));
            case 3:
                return [...newAnimalList].sort((a, b) => compareNullableNumber(a.daysFromInsem, b.daysFromInsem));
        }

        return null;
    }

    const setFocusOnSearch = () => {
        document?.getElementById("sent-barcode-search")?.focus();
    }

    const handlePregCheckOrderChange = (euIdentifier: string) => {
        if (barcodeSending != null && barcodeSending.animals != null) {
            var newAnimalList: SentBarcodeAnimalDto[] = barcodeSending?.animals?.map((item) => {
                if (item.euIdentifier === euIdentifier) {
                    const updatedItem = structuredClone(item);
                    updatedItem.newPregCheckOrder = !item.newPregCheckOrder
                    return updatedItem;
                }
                return item;
            });

            var updatedSending = structuredClone(barcodeSending);
            updatedSending.animals = newAnimalList;
           
            dispatch(setSelectedBarcodeSending(updatedSending));
        }
    }

    const save = () => {
        if (barcodeSending != null && barcodeSending.testMilkDate != null) {
            const command = new UpdatePregnancyCheckOrdersCommand();
            command.testMilkDate = new Date(barcodeSending?.testMilkDate);
            command.herdId = selectedHerd?.idkar;
            command.sendTo = barcodeSendTo;
            command.data = barcodeSending?.animals?.map(a => new BarcodeDataToSaveDto({ bovineId: a.bovineId, barcode: a.barcode, pregCheckOrder: a.newPregCheckOrder, hasChanged: a.newPregCheckOrder !== a.pregCheckOrdered }));

            dispatch(startLoading());
            saveSentBarcodes(command)
              .then(() => {
                  toast.success(`${t("Notification.BarcodesSend")}`, {
                      autoClose: getAutoCloseTime(),
                  });
              }).finally(() => {
                  if(selectedHerd?.idkar != null)
                      fetchSentBarcodes(selectedHerd?.idkar);
                  dispatch(stopLoading());
                  toggleSentBarcodesModal();
              });
        }
    }

    return (
        <Modal
            className="sent-barcodes-modal"
            isOpen={showSentBarcodesModal}
            size="m"
            toggle={toggleSentBarcodesModal}
        >
            <ModalHeader
                toggle={toggleSentBarcodesModal}
            >
                <Row>
                    <Col><img width="60" src="./img/ProAgria_logo.png" alt="MobiLink" /></Col>
                </Row>
                <Row>
                    <Col>{t("Edit.SendBarcodes")}</Col>
                </Row>
            </ModalHeader>
            <ModalBody>
                {barcodeSending != null && (
                    <div>
                        <Row>
                            <Col>
                                {t("Edit.BarcodesSent") + ' ' + new Date(barcodeSending.sentDate).toLocaleTimeString('fi-FI', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'}) }
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col>{t("Edit.Info") }</Col>
                        </Row>
                        <Row className="mb-3">
                            <Col>
                                {t("Edit.TestsSum") + ' ' + barcodeSending.animals?.filter(a => a.newPregCheckOrder).length}
                            </Col>
                            <Col xs="5">
                                <Button
                                    disabled={barcodeSending.animals == null || !barcodeSending.animals.some(a => a.newPregCheckOrder !== a.pregCheckOrdered) }
                                    variant="contained"
                                    onClick={save }
                                >
                                    {isLoading ? (<Spinner
                                        style={{ width: "1.5rem", height: "1.5rem" }}
                                        color="primary"
                                    />) : (<span><SendOutlinedIcon /> {t("Edit.Send")}</span>)}
                                </Button>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col>{t("List.Search")}</Col>
                            <Col>
                                <input
                                    id="sent-barcode-search"
                                    type="search"
                                    onChange={(e) => setSearchValue(e.target.value)}
                                />
                            </Col>
                        </Row>
                    </div>
                )}
                {currentAnimalList != null && (
                    <div>
                        {
                            currentAnimalList.map((item) => {
                                return (
                                    <SentBarcode
                                        sentBarcode={item}
                                        onPregCheckToggle={handlePregCheckOrderChange }
                                        key={item.euIdentifier}
                                    />
                                )
                            })
                        }
                    </div>
                )}
            </ModalBody>
            <Fab color="primary" size="medium" onClick={setFocusOnSearch}>
                <SearchOutlinedIcon />
            </Fab>
        </Modal>
    );
}

export default SentBarcodesModal;
